"use client"

import * as React from "react"
import { Link, useLocation } from "react-router-dom"

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar"
import { isNotificationsCentrePathname } from "@/constants/notificationsCentre"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import routes from "@/routes"
import { getBreadcrumbTrail } from "@/utils/breadcrumbPageTitle"
import type { RouteConfigArray } from "@/types/routes"
import { useShadcnInsetBreadcrumbContext } from "@/contexts/ShadcnInsetBreadcrumbContext"
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext"

const BREADCRUMB_BRAND = "Tessera"
const BREADCRUMB_HOME = "/dashboard"

/**
 * Inset breadcrumb row. Sidebar header uses `px-2` (nav edge alignment) and
 * matching vertical padding / inner height (`py-3` + `h-12` expanded).
 */
export function ShadcnSidebarInsetHeader() {
  const { pathname, search } = useLocation()
  const { state } = useSidebar()
  const isMobile = useIsMobile()
  const expanded = state === "expanded"
  const hideSidebarTrigger =
    !isMobile && isNotificationsCentrePathname(pathname)
  const { trailOverride } = useShadcnInsetBreadcrumbContext()
  const trail =
    trailOverride ??
    getBreadcrumbTrail(routes as RouteConfigArray, pathname, search)
  const { actions: headerActions, setMountEl } = useShadcnInsetHeaderActions()

  return (
    <header
      data-slot="sidebar-inset-header"
      className={cn(
        "flex w-full shrink-0 border-b border-border bg-background transition-[padding] duration-200 ease-linear dark:bg-sidebar dark:text-sidebar-foreground",
        expanded ? "px-4 py-3" : "px-2 py-2"
      )}
    >
      <div
        className={cn(
          "flex w-full items-center gap-2",
          expanded ? "h-12" : "h-8"
        )}
      >
        {hideSidebarTrigger ? null : (
          <>
            <SidebarTrigger
              className={cn(
                "-ml-1 size-8 shrink-0 text-[color:var(--brand-tropical-surf)]",
                "[&_svg]:size-4 [&_svg]:text-current"
              )}
            />
            <Separator
              orientation="vertical"
              decorative
              className="mx-0 mr-2 h-4 w-px shrink-0 self-center bg-border data-[orientation=vertical]:!h-4 data-[orientation=vertical]:!self-center"
            />
          </>
        )}
        {isMobile ? (
          <div className="min-w-0 flex-1" aria-hidden />
        ) : (
          <Breadcrumb className="min-w-0 flex-1 overflow-hidden">
            <BreadcrumbList className="dark:text-white">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to={BREADCRUMB_HOME}
                    className="text-muted-foreground dark:text-white"
                  >
                    {BREADCRUMB_BRAND}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="dark:text-white [&>svg]:text-current" />
              {trail.map((crumb, index) => {
                const isLast = index === trail.length - 1
                return (
                  <React.Fragment key={`${crumb.label}-${index}`}>
                    {index > 0 ? (
                      <BreadcrumbSeparator className="dark:text-white [&>svg]:text-current" />
                    ) : null}
                    <BreadcrumbItem>
                      {isLast || !crumb.to ? (
                        <BreadcrumbPage className="truncate">
                          {crumb.label}
                        </BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link
                            to={crumb.to}
                            className="truncate text-muted-foreground dark:text-white"
                          >
                            {crumb.label}
                          </Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                )
              })}
            </BreadcrumbList>
          </Breadcrumb>
        )}
        <div
          ref={setMountEl}
          data-slot="sidebar-inset-header-actions"
          className="ml-auto flex shrink-0 items-center gap-2"
        >
          {headerActions}
        </div>
      </div>
    </header>
  )
}
